<template>
  <div v-if="showModuloForm === false">
    <MarketingModules></MarketingModules>
  </div>
  <div class="page" v-else-if="showModuloForm === true">
    <v-row class="notificacoes-layout">
      <v-col cols="12" md="5" class="notificacoes-col-destinatarios">
        <Panel lineColor="var(--primary-color)" class="notificacoes-panel">
          <div class="section-header">
            <h3 class="section-title">Destinatários</h3>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <FormButton
                  type="primario"
                  color="primario"
                  outlined
                  icon
                  class="btn-novo-destinatario"
                  v-on="on"
                  @click="abrirModalDestinatario()"
                >
                  <v-icon size="16">fas fa-plus</v-icon>
                </FormButton>
              </template>
              Novo destinatário
            </v-tooltip>
          </div>

          <p class="section-subtitle">
            Cadastre os e-mails da equipe que podem receber alertas internos.
          </p>

          <v-data-table
            :headers="headersDestinatarios"
            :items="destinatarios"
            :loading="loadingDestinatarios"
            hide-default-footer
            disable-pagination
            class="destinatarios-table"
            no-data-text="Nenhum destinatário cadastrado"
          >
            <template v-slot:item.ativo="{ item }">
              <Chip
                v-if="item.ativo"
                color="green"
                text="Ativo"
                :small="true"
              />
              <Chip v-else color="cyan" text="Inativo" :small="true" />
            </template>
            <template v-slot:item.opcoes="{ item }">
              <FormButtonMenu
                icon
                :options="tableOptions"
                @click="op => opcoesDestinatario(item, op)"
              >
                <v-icon size="15">fas fa-ellipsis-v</v-icon>
              </FormButtonMenu>
            </template>
          </v-data-table>
        </Panel>
      </v-col>

      <v-col cols="12" md="7" class="notificacoes-col-eventos">
        <Panel
          lineColor="var(--secondary-color)"
          class="notificacoes-panel eventos-panel"
        >
          <div class="section-intro">
            <h3 class="section-title">Eventos monitorados</h3>
            <p class="section-subtitle">
              Escolha quais eventos da operação devem gerar alertas por e-mail.
            </p>
            <p class="nota-informativa">
              É possível vincular múltiplos e-mails em cada evento.
            </p>
          </div>

          <div
            v-for="evento in eventosDraft"
            :key="evento.alias"
            class="evento-card"
          >
            <div class="evento-header">
              <FormSwitch v-model="evento.habilitado" :label="evento.nome" />
            </div>
            <p class="evento-descricao">{{ evento.descricao }}</p>

            <FormSelect
              label="Destinatários"
              v-model="evento.iddestinatarios"
              :options="destinatarioOptions"
              multiple
              :disabled="!destinatarioOptions.length"
              :placeholder="
                destinatarioOptions.length
                  ? 'Selecione os destinatários'
                  : 'Cadastre ao menos um destinatário'
              "
            />

            <template v-if="isElasticMail">
              <FormSelect
                label="Template ElasticMail"
                v-model="evento.template_nome"
                :options="templateOptions(evento)"
                :disabled="!templateOptions(evento).length"
                :placeholder="
                  templateOptions(evento).length
                    ? 'Selecione o template'
                    : 'Associe um template em Marketing → Templates de e-mail'
                "
              />
            </template>
            <p v-else class="smtp-aviso">
              O texto do e-mail é configurado em
              <router-link to="/fornecedor/configuracao/mensagens-automaticas">
                Configurações → Mensagens automáticas
              </router-link>
              .
            </p>
          </div>

          <v-row justify="end" class="eventos-actions pa-3">
            <FormButton
              type="perigo"
              text="Cancelar"
              @click="cancelarEventos"
            />
            <FormButton
              type="submissao"
              text="Salvar configurações"
              :loading="salvandoEventos"
              @click="salvarEventos"
              style="margin-left: 5px"
            />
          </v-row>
        </Panel>
      </v-col>
    </v-row>

    <transition name="slide-popup" mode="out-in">
      <DestinatarioModalForm
        v-if="modalDestinatario.open"
        :idfornecedor="getFornecedorId"
        :destinatario="modalDestinatario.destinatario"
        @close="fecharModalDestinatario"
        @saved="onDestinatarioSalvo"
      />
    </transition>
  </div>
</template>

<script>
import Panel from "@/components/Panel";
import FormButton from "@/components/form/FormButton";
import FormButtonMenu from "@/components/form/FormButtonMenu";
import FormSelect from "@/components/form/FormSelect";
import FormSwitch from "@/components/form/FormSwitch";
import Chip from "@/components/Chip";
import MarketingModules from "@/components/MarketingModules";
import DestinatarioModalForm from "./forms/DestinatarioModalForm";
import { mapGetters } from "vuex";
import {
  NOTIFICACAO_ADMIN_LISTAR_DESTINATARIOS,
  NOTIFICACAO_ADMIN_EXCLUIR_DESTINATARIO,
  NOTIFICACAO_ADMIN_OBTER_EVENTOS,
  NOTIFICACAO_ADMIN_SALVAR_EVENTOS,
  NOTIFICACAO_ADMIN_EMAIL_CONFIG,
  NOTIFICACAO_ADMIN_TEMPLATES_ESCOLHIDOS
} from "@/store/actions/notificacoesAdministrativas";
import { ModulesEnum } from "@/store/actions/modulesEnum";
import * as _ from "lodash";

const TEMPLATE_POR_EVENTO = {
  novo_cadastro_clientes: "notificacaoAdminNovoCadastro",
  pedido_emitido: "notificacaoAdminPedidoEmitido",
  alerta_integracao: "notificacaoAdminAlertaIntegracao"
};

export default {
  name: "NotificacoesAdministrativas",
  components: {
    Panel,
    FormButton,
    FormButtonMenu,
    FormSelect,
    FormSwitch,
    Chip,
    MarketingModules,
    DestinatarioModalForm
  },
  data: () => ({
    destinatarios: [],
    eventosDraft: [],
    eventosSnapshot: [],
    templatesEscolhidos: {},
    serviceEmail: "ELASTIC_MAIL",
    loadingDestinatarios: false,
    salvandoEventos: false,
    modalDestinatario: {
      open: false,
      destinatario: null
    },
    headersDestinatarios: [
      { text: "Descrição", value: "descricao" },
      { text: "E-mail", value: "email" },
      { text: "Status", value: "ativo", align: "center" },
      {
        text: "Opções",
        value: "opcoes",
        align: "center",
        sortable: false
      }
    ],
    tableOptions: [
      { text: "Editar", value: "editar" },
      { text: "Excluir", value: "excluir" }
    ],
    modulosForm: [
      ModulesEnum.PAINEL_CLIENTE,
      ModulesEnum.ORCAMENTO_WEB,
      ModulesEnum.ORCAMENTO_APP,
      ModulesEnum.VENDAMAIS_WEB,
      ModulesEnum.VENDAMAIS_APP,
      ModulesEnum.WEB_COMMERCE,
      ModulesEnum.COMMERCE_APP,
      ModulesEnum.LIFEGROWTH
    ]
  }),
  computed: {
    ...mapGetters(["getFornecedorId", "moduloFornecedorAtivo"]),
    showModuloForm() {
      return this.moduloFornecedorAtivo(this.modulosForm);
    },
    isElasticMail() {
      return this.serviceEmail === "ELASTIC_MAIL";
    },
    destinatarioOptions() {
      return this.destinatarios.map(destinatario => ({
        value: destinatario.id,
        text: `${destinatario.descricao} (${destinatario.email})`
      }));
    },
    destinatariosPorId() {
      return _.keyBy(this.destinatarios, "id");
    }
  },
  created() {
    this.carregarDados();
  },
  methods: {
    async carregarDados() {
      await Promise.all([
        this.carregarConfigEmail(),
        this.carregarTemplatesEscolhidos(),
        this.carregarDestinatarios(),
        this.carregarEventos()
      ]);
    },
    async carregarConfigEmail() {
      try {
        const config = await this.$store.dispatch(
          NOTIFICACAO_ADMIN_EMAIL_CONFIG
        );
        this.serviceEmail = _.get(config, "service", "ELASTIC_MAIL");
      } catch (err) {
        // erro tratado na action
      }
    },
    async carregarTemplatesEscolhidos() {
      try {
        this.templatesEscolhidos =
          (await this.$store.dispatch(
            NOTIFICACAO_ADMIN_TEMPLATES_ESCOLHIDOS
          )) || {};
      } catch (err) {
        // erro tratado na action
      }
    },
    async carregarDestinatarios() {
      this.loadingDestinatarios = true;
      try {
        this.destinatarios =
          (await this.$store.dispatch(
            NOTIFICACAO_ADMIN_LISTAR_DESTINATARIOS
          )) || [];
      } catch (err) {
        // erro tratado na action
      } finally {
        this.loadingDestinatarios = false;
      }
    },
    mapearEventoParaDraft(evento) {
      return {
        alias: evento.alias,
        nome: evento.nome,
        descricao: evento.descricao,
        habilitado: Boolean(evento.habilitado),
        template_nome: evento.template_nome || null,
        iddestinatarios: (evento.destinatarios || []).map(
          destinatario => destinatario.id
        )
      };
    },
    aplicarSnapshotEventos(eventos) {
      const draft = (eventos || []).map(evento =>
        this.mapearEventoParaDraft(evento)
      );
      this.eventosDraft = draft;
      this.eventosSnapshot = _.cloneDeep(draft);
    },
    async carregarEventos() {
      try {
        const eventos = await this.$store.dispatch(
          NOTIFICACAO_ADMIN_OBTER_EVENTOS
        );
        this.aplicarSnapshotEventos(eventos || []);
      } catch (err) {
        // erro tratado na action
      }
    },
    templateOptions(evento) {
      const chave = TEMPLATE_POR_EVENTO[evento.alias];
      const associado = chave ? this.obterNomeTemplateAssociado(chave) : "";
      const opcoes = [];

      if (associado) {
        opcoes.push({ value: associado, text: associado });
      }

      const atual = (evento.template_nome || "").trim();
      if (atual && !opcoes.some(item => item.value === atual)) {
        opcoes.push({ value: atual, text: atual });
      }

      return opcoes;
    },
    obterNomeTemplateAssociado(chave) {
      const valor = this.templatesEscolhidos[chave];
      if (!valor) return "";
      return (typeof valor === "string" ? valor : valor.nome || "").trim();
    },
    abrirModalDestinatario(destinatario = null) {
      this.modalDestinatario = {
        open: true,
        destinatario
      };
    },
    fecharModalDestinatario() {
      this.modalDestinatario = {
        open: false,
        destinatario: null
      };
    },
    atualizarDestinatarioLocal(destinatario) {
      if (!destinatario || !destinatario.id) return;

      const index = this.destinatarios.findIndex(
        item => item.id === destinatario.id
      );

      if (index >= 0) {
        this.destinatarios = [
          ...this.destinatarios.slice(0, index),
          destinatario,
          ...this.destinatarios.slice(index + 1)
        ];
      } else {
        this.destinatarios = [...this.destinatarios, destinatario];
      }
    },
    removerDestinatarioLocal(id) {
      this.destinatarios = this.destinatarios.filter(item => item.id !== id);

      this.eventosDraft = this.eventosDraft.map(evento => {
        const iddestinatarios = (evento.iddestinatarios || []).filter(
          destId => destId !== id
        );
        const habilitado =
          evento.habilitado && iddestinatarios.length === 0
            ? false
            : evento.habilitado;

        return {
          ...evento,
          iddestinatarios,
          habilitado
        };
      });
      this.eventosSnapshot = _.cloneDeep(this.eventosDraft);
    },
    onDestinatarioSalvo(destinatario) {
      this.fecharModalDestinatario();
      this.atualizarDestinatarioLocal(destinatario);
    },
    opcoesDestinatario(row, op) {
      const acoes = {
        editar: () => this.abrirModalDestinatario(row),
        excluir: () => this.excluirDestinatario(row)
      };
      acoes[op.value]();
    },
    excluirDestinatario(destinatario) {
      this.$swal
        .fire({
          title: "Exclusão de destinatário",
          text: `Deseja realmente remover "${destinatario.descricao}"? Os vínculos com eventos serão removidos.`,
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Confirmar",
          cancelButtonText: "Cancelar",
          reverseButtons: true
        })
        .then(async result => {
          if (!result.value) return;

          try {
            await this.$store.dispatch(NOTIFICACAO_ADMIN_EXCLUIR_DESTINATARIO, {
              idfornecedor: this.getFornecedorId,
              id: destinatario.id
            });
            this.$vueOnToast.pop(
              "success",
              "Destinatário excluído com sucesso"
            );
            this.removerDestinatarioLocal(destinatario.id);
          } catch (err) {
            // erro tratado na action
          }
        });
    },
    cancelarEventos() {
      this.eventosDraft = _.cloneDeep(this.eventosSnapshot);
    },
    validarEventos() {
      for (const evento of this.eventosDraft) {
        if (!evento.habilitado) continue;

        const ids = evento.iddestinatarios || [];
        const temAtivo = ids.some(id => {
          const destinatario = this.destinatariosPorId[id];
          return destinatario && destinatario.ativo;
        });

        if (!temAtivo) {
          return `O evento ${evento.nome} exige ao menos um destinatário ativo`;
        }

        if (this.isElasticMail && !(evento.template_nome || "").trim()) {
          return `O evento ${evento.nome} exige um template ElasticMail`;
        }
      }

      return null;
    },
    montarPayloadEventos() {
      return this.eventosDraft.map(evento => ({
        alias: evento.alias,
        habilitado: Boolean(evento.habilitado),
        template_nome: this.isElasticMail
          ? (evento.template_nome || "").trim() || null
          : null,
        iddestinatarios: evento.iddestinatarios || []
      }));
    },
    async salvarEventos() {
      const erro = this.validarEventos();
      if (erro) {
        this.$vueOnToast.pop("error", erro);
        return;
      }

      this.salvandoEventos = true;
      try {
        const eventos = await this.$store.dispatch(
          NOTIFICACAO_ADMIN_SALVAR_EVENTOS,
          this.montarPayloadEventos()
        );
        this.aplicarSnapshotEventos(eventos || []);
        this.$vueOnToast.pop("success", "Configurações salvas com sucesso");
      } catch (err) {
        // erro tratado na action
      } finally {
        this.salvandoEventos = false;
      }
    }
  }
};
</script>

<style scoped>
.notificacoes-layout {
  align-items: stretch;
}

.notificacoes-col-destinatarios,
.notificacoes-col-eventos {
  display: flex;
}

.notificacoes-panel {
  flex: 1;
  width: 100%;
}

.notificacoes-col-eventos .eventos-panel {
  margin-top: 0;
}

@media (max-width: 959px) {
  .notificacoes-col-eventos .eventos-panel {
    margin-top: 20px;
  }
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}

.section-header .section-title {
  margin-bottom: 0;
}

.btn-novo-destinatario {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
}

.section-intro,
.section-title,
.section-subtitle,
.nota-informativa {
  text-align: left;
  border-radius: 8px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
}

.section-subtitle {
  color: #6a6c6f;
  margin-bottom: 16px;
  font-size: 16px;
}

.nota-informativa {
  background: #f5f7fa;
  border-left: 3px solid var(--primary-color);
  padding: 10px 12px;
  margin-bottom: 20px;
  color: #6a6c6f;
}

.evento-card {
  border: 1px solid #e4e5e7;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.evento-header {
  margin-bottom: 8px;
}

.evento-descricao {
  color: #6a6c6f;
  margin-bottom: 16px;
}

.smtp-aviso {
  color: #6a6c6f;
  font-size: 14px;
  margin-top: 8px;
}

.eventos-actions {
  margin-top: 8px;
}

.destinatarios-table {
  margin-top: 8px;
}
</style>
